import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import ProductTariffFieldErrors from 'Components/ProductTariffFieldErrors/ProductTariffFieldErrors'
import StyledLabel from 'Components/StyledLabel'
import { SELECTED_VAT_DEFINITION_PARAMETER_KEY } from 'Services/ProductManagement/Features/VATDefinition/Hooks/useVATDefinitionsWithParams'
import {
  VATCode,
  type VATDefinition,
  type VatPercentage
} from 'Services/ProductManagement/Features/VATDefinition/types'
import {
  useCreateSingleVatDefinitionMutation,
  useUpdateSingleVatDefinitionMutation
} from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import type { ProductTariffError, ProductTariffViolation } from 'Util/ApiClient'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamToUrl } from 'Util/url'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface FieldErrors {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  title: [],
  enabled: [],
  description: [],
  vat_code: [],
  vat_percent: [],
  active_start: [],
  active_end: []
}

interface VATDefinitionFormProps {
  successBaseUrl: string
  vatDefinition?: VATDefinition
}

export default function VATDefinitionForm({
  vatDefinition,
  successBaseUrl
}: Readonly<VATDefinitionFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!vatDefinition

  const [createSingleVatDefinition, { isLoading: isCreateLoading }] =
    useCreateSingleVatDefinitionMutation()
  const [updateSingleVatDefinition, { isLoading: isUpdateLoading }] =
    useUpdateSingleVatDefinitionMutation()

  const [vatDefinitionDetails, setVatDefinitionDetails] = useState<Partial<VATDefinition>>(
    (isEdit && vatDefinition) || {}
  )

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof VATDefinition
  ) => {
    if (value !== undefined) {
      setVatDefinitionDetails(prevState => ({ ...prevState, [field]: value }) as VATDefinition)
    }
  }

  const handleVatPercentageChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof VatPercentage,
    index: number
  ) => {
    if (value !== undefined) {
      setVatDefinitionDetails(prevState => {
        const vatPercentages = [...(prevState.vat_percentages ?? [])]
        const vatPercentage = vatPercentages[index] ?? {}
        vatPercentages[index] = { ...vatPercentage, [field]: value }
        return {
          ...prevState,
          vat_percentages: vatPercentages
        } as VATDefinition
      })
    }
  }

  const removeVatPercentage = (index: number) => {
    setVatDefinitionDetails(prevState => {
      const vatPercentages = [...(prevState.vat_percentages ?? [])]
      vatPercentages.splice(index, 1)
      return { ...prevState, vat_percentages: vatPercentages } as VATDefinition
    })
  }

  const addVatPercentage = () => {
    setVatDefinitionDetails(prevState => {
      const vatPercentages = [...(prevState.vat_percentages ?? [])]
      vatPercentages.push({ vat_code: VATCode.VAT_0, vat_percent: 0 })
      return { ...prevState, vat_percentages: vatPercentages } as VATDefinition
    })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const vatDefinitionData = {
      ...vatDefinitionDetails
    }

    if (vatDefinition) {
      updateSingleVatDefinition({
        ...vatDefinitionData,
        id: vatDefinition.id
      })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`VAT definition successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
          const { violations } = errorResponse
          if (violations && violations.length > 0) {
            setValidationErrors(violations)
          }
          SnackbarUtils.error(t`There was an error updating the VAT definition.`)
        })
      return
    }
    createSingleVatDefinition(vatDefinitionData)
      .unwrap()
      .then((vatDefinitionResponse: VATDefinition) => {
        SnackbarUtils.success(t`VAT definition successfully created.`)
        navigate(
          appendQueryParamToUrl(
            successBaseUrl,
            SELECTED_VAT_DEFINITION_PARAMETER_KEY,
            vatDefinitionResponse.id
          )
        )
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        }
        SnackbarUtils.error(t`There was an error creating the VAT definition.`)
      })
  }

  const setValidationErrors = (violations: ProductTariffViolation[]) => {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[violation.field] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const vatCodeChoices = [
    { label: t`VAT_00`, value: VATCode.VAT_0 },
    { label: t`VAT_01`, value: VATCode.VAT_01 },
    { label: t`VAT_02`, value: VATCode.VAT_02 },
    { label: t`VAT_99`, value: VATCode.VAT_99 }
  ]

  const isSubmitLoading = isCreateLoading || isUpdateLoading

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex' }}>
            <FormControl sx={{ minWidth: 240, mr: 3 }} size="small">
              <TextField
                name="title"
                required
                label={t`Title`}
                size="small"
                value={vatDefinitionDetails?.title}
                error={fieldErrors.title.length > 0}
                onChange={e => handleChange(e.target.value, 'title')}
              />
              <ProductTariffFieldErrors error={fieldErrors.title} identifier="title" />
            </FormControl>
          </Box>
          <Box>
            <StyledLabel>{t`VAT percentages`}</StyledLabel>
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            {vatDefinitionDetails?.vat_percentages?.map((vatPercentage, index) => (
              <Box
                key={`key-${vatDefinitionDetails.id}-${index}`}
                display="flex"
                alignItems="center"
              >
                <FormControl sx={{ minWidth: 240, mr: 3 }} required>
                  <InputLabel size="small">{t`VAT code`}</InputLabel>
                  <Select
                    name="vat_code"
                    required
                    size="small"
                    label={t`VAT code`}
                    value={vatPercentage.vat_code ?? ''}
                    error={fieldErrors[`vat_code_${index}`]?.length > 0}
                    onChange={e =>
                      handleVatPercentageChange(e.target.value as VATCode, 'vat_code', index)
                    }
                  >
                    {vatCodeChoices.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <ProductTariffFieldErrors error={fieldErrors.vat_code} identifier="vat_code" />
                </FormControl>
                <FormControl sx={{ minWidth: 240, mr: 3 }} size="small">
                  <TextField
                    name="vat_percent"
                    type="number"
                    required
                    label={t`VAT percent`}
                    size="small"
                    value={vatPercentage.vat_percent ?? ''}
                    error={fieldErrors[`vat_percent_${index}`]?.length > 0}
                    onChange={e => handleVatPercentageChange(e.target.value, 'vat_percent', index)}
                  />
                  <ProductTariffFieldErrors
                    error={fieldErrors.vat_percent}
                    identifier="vat_percent"
                  />
                </FormControl>
                <CustomButton
                  onClick={() => removeVatPercentage(index)}
                  size="small"
                  variant="text"
                  color="error"
                  startIcon={<CloseIcon />}
                >
                  {t`Remove row`}
                </CustomButton>
              </Box>
            ))}
          </Box>
          <CustomButton
            onClick={addVatPercentage}
            startIcon={<AddIcon />}
            variant="text"
            size="small"
            sx={{ mt: 2 }}
          >{t`Add VAT percentage`}</CustomButton>
          <Box sx={{ my: 4 }}>
            <StyledLabel>{t`Other`}</StyledLabel>
          </Box>
          <Box sx={{ my: 4 }}>
            <FormControl sx={{ minWidth: 420, mr: 3 }} size="small">
              <TextField
                name="description"
                label={t`Description`}
                size="small"
                value={vatDefinitionDetails?.description}
                onChange={e => handleChange(e.target.value, 'description')}
                multiline
                rows={5}
              />
            </FormControl>
          </Box>
          <CustomLoadingButton
            type="submit"
            size="large"
            isLoading={isSubmitLoading}
            iconPosition="end"
          >
            {t`Save VAT definition`}
          </CustomLoadingButton>
        </Box>
      </Grid>
    </Grid>
  )
}
